import { AxiosError } from 'axios';

import {
  NotificationTypes,
  SubscriptionResponse,
  Subscription,
} from '@dynamika-shared/types/subscription';
import { RESTClient } from './client';
import { API_URL } from './apiDictionary';

export type FetchSubscriptionsParams = {
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
  scope?: string | string[];
  type?: string | string[];
};

export type ChannelType = 'TEAMS' | 'EMAIL';

export type UpdateSubscriptionRequest = {
  notificationType: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  channelType: ChannelType;
  channelUserIdentifier: string;
  subscribed: boolean;
  filter: {
    type: string;
    owners: string[];
    sites: string[];
  };
};

export const fetchSubscriptions = async (
  clinicalTrialId: string,
  notificationType?: NotificationTypes,
  {
    page = 1,
    size = 10,
    sort,
    search,
    scope,
    type,
  }: FetchSubscriptionsParams = {}
): Promise<SubscriptionResponse> => {
  const scopeParam = Array.isArray(scope) ? scope.join(',') : scope;
  const typeParam = Array.isArray(type) ? type.join(',') : type;

  const response = await RESTClient.get<SubscriptionResponse>(
    API_URL.pmSubscriptions,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      params: {
        notificationType: notificationType,
        page: page - 1,
        size: size,
        sort: sort,
        search: search,
        scope: scopeParam,
        subscriptionType: typeParam,
      },
    }
  );
  return response.data;
};


/**
 * One recipient at one site — the backend's `SiteNotificationRecipientDto`.
 * All optional so a narrow payload blanks cells instead of throwing.
 */
export type RecipientContent = {
  userId?: string;
  fullName?: string;
  channelIdentifier?: string;
  /** `USER` or `EMAIL`. */
  recipientType?: string;
  /** Backend role code (`PROJECT_MANAGER`) or an already-readable label. */
  recipientRoles?: string;
  notificationTypes?: NotificationTypes[];
};

export type FetchRecipientsParams = {
  siteId: string;
  search?: string;
  notificationTypes?: NotificationTypes[];
};

/** Same refinements as the listing, but every one of them optional. */
export type ExportRecipientsParams = Partial<FetchRecipientsParams>;

export const fetchRecipients = async (
  clinicalTrialId: string,
  { siteId, search, notificationTypes }: FetchRecipientsParams
): Promise<RecipientContent[]> => {
  const response = await RESTClient.get<RecipientContent[]>(
    API_URL.pmRecipients,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      params: {
        siteId,
        search: search || undefined,
        // NOTE: joined, not passed as an array — axios would serialise it as
        // `notificationTypes[]=…`, which the backend does not bind.
        notificationTypes: notificationTypes?.length
          ? notificationTypes.join(',')
          : undefined,
      },
    }
  );

  return response.data ?? [];
};

export type BulkSubscriptionRowStatus = 'CREATED' | 'SKIPPED' | 'REJECTED';

/** One CSV row's outcome — the backend's `BulkSubscriptionRowResultDto`. */
export type BulkSubscriptionRowResult = {
  /** 1-based position in the uploaded file. */
  rowNumber?: number;
  email?: string;
  status?: BulkSubscriptionRowStatus;
  /** Why the row was skipped or rejected; empty for a created row. */
  reasons?: string[];
};

/**
 * The backend's `BulkSubscriptionUploadResultDto` — typed in full but not
 * rendered yet; the breakdown is D7-7650. All optional, as above.
 */
export type BulkSubscriptionUploadResult = {
  totalRowCount?: number;
  createdCount?: number;
  skippedCount?: number;
  rejectedCount?: number;
  rowResults?: BulkSubscriptionRowResult[];
  processingComplete?: boolean;
  failedAtRowNumber?: number;
  failureReason?: string;
};

/**
 * Bulk-create from one CSV; the server validates. `Content-Type` is unset so
 * the browser writes it and includes the multipart boundary.
 */
export const bulkUploadSubscriptions = async (
  clinicalTrialId: string,
  file: File
): Promise<BulkSubscriptionUploadResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await RESTClient.post<BulkSubscriptionUploadResult>(
    API_URL.pmSubscriptionsBulk,
    formData,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );

  return response.data;
};

/**
 * `responseType: 'blob'` hits error bodies too, so `data.detail` reads
 * `undefined`. Rewrites the body in place; non-JSON is left for the fallback.
 */
const unwrapBlobErrorBody = async (error: unknown): Promise<void> => {
  if (!(error instanceof AxiosError)) return;

  const body = error.response?.data;

  if (!(body instanceof Blob)) return;

  try {
    error.response!.data = JSON.parse(await body.text());
  } catch {
    // Not JSON — leave the blob in place for the generic fallback.
  }
};

/**
 * The blank CSV — columns are the server's, so they change without a release.
 * Any `responseType` but `'blob'` lets axios coerce and corrupt the bytes.
 */
export const downloadBulkUploadTemplate = async (
  clinicalTrialId: string
): Promise<Blob> => {
  try {
    const response = await RESTClient.get<Blob>(
      API_URL.pmSubscriptionsBulkTemplate,
      {
        headers: {
          'x-dynamika-clinical-trial-id': clinicalTrialId,
        },
        responseType: 'blob',
      }
    );

    return response.data;
  } catch (error) {
    await unwrapBlobErrorBody(error);

    throw error;
  }
};

/**
 * Exports the list as refined on screen — every filter optional, blanks
 * dropped, `notificationTypes` comma-joined since axios would send `[]=…`.
 */
export const exportRecipients = async (
  clinicalTrialId: string,
  { siteId, search, notificationTypes }: ExportRecipientsParams = {}
): Promise<Blob> => {
  try {
    const response = await RESTClient.get<Blob>(API_URL.pmRecipientsExport, {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      params: {
        siteId: siteId || undefined,
        search: search || undefined,
        notificationTypes: notificationTypes?.length
          ? notificationTypes.join(',')
          : undefined,
      },
      responseType: 'blob',
    });

    return response.data;
  } catch (error) {
    await unwrapBlobErrorBody(error);

    throw error;
  }
};

export const updateSubscription = async (
  subscriptionIds: string[],
  clinicalTrialId: string,
): Promise<SubscriptionResponse> => {
  const response = await RESTClient.put<SubscriptionResponse>(
    API_URL.pmSubscribe,
    { subscriptionIds },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
  return response.data;
};

export const deleteSubscription = async (
  subscriptionIds: string[],
  clinicalTrialId: string
): Promise<SubscriptionResponse> => {
  const response = await RESTClient.put<SubscriptionResponse>(
    API_URL.pmUnsubscribe,
    { subscriptionIds },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
  return response.data;
};

export const createSubscription = async (
  clinicalTrialId: string,
  body: UpdateSubscriptionRequest
): Promise<Subscription> => {
  const response = await RESTClient.post<Subscription>(
    API_URL.pmSubscriptions,
    body,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
  return response.data;
};

export const deleteSubscriptionById = async (
  subscriptionId: string,
  clinicalTrialId: string
): Promise<SubscriptionResponse> => {
  const response = await RESTClient.delete<SubscriptionResponse>(
    API_URL.pmSubscription.replace(':subscriptionId', subscriptionId),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
  return response.data;
};

export const editSubscriptionById = async (
  subscriptionId: string,
  clinicalTrialId: string,
  body: UpdateSubscriptionRequest,
): Promise<SubscriptionResponse> => {
  const response = await RESTClient.put<SubscriptionResponse>(
    API_URL.pmSubscription.replace(':subscriptionId', subscriptionId),
    body,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
  return response.data;
};
